// 개발자에게 문의하기 다이얼로그 컴포넌트
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Alert,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { addContactInquiry } from '../../services/firebaseService';

function ContactDeveloperDialog({ open, onClose }) {
  const { t } = useTranslation(['common', 'errors']);

  // 상태 관리
  const [category, setCategory] = useState('general');
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [alert, setAlert] = useState({ show: false, type: 'success', message: '' });

  // 초기화
  const resetForm = () => {
    setCategory('general');
    setTitle('');
    setContent('');
    setEmail('');
    setAlert({ show: false, type: 'success', message: '' });
  };

  // 닫기
  const handleClose = () => {
    if (!isSubmitting) {
      resetForm();
      onClose();
    }
  };

  // 전송
  const handleSubmit = async () => {
    // 입력 검증
    if (!title.trim() || !content.trim()) {
      setAlert({
        show: true,
        type: 'error',
        message: t('contact.requiredFields'),
      });
      return;
    }

    // 길이 제한
    if (title.length > 100) {
      setAlert({
        show: true,
        type: 'error',
        message: t('contact.titleTooLong'),
      });
      return;
    }

    if (content.length > 2000) {
      setAlert({
        show: true,
        type: 'error',
        message: t('contact.contentTooLong'),
      });
      return;
    }

    // 이메일 형식 검증 (입력된 경우에만)
    if (email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setAlert({
        show: true,
        type: 'error',
        message: t('contact.invalidEmail'),
      });
      return;
    }

    try {
      setIsSubmitting(true);
      setAlert({ show: false, type: 'success', message: '' });

      // Firebase에 문의 추가
      await addContactInquiry({
        category,
        title: title.trim(),
        content: content.trim(),
        email: email.trim() || null,
      });

      // 성공 알림
      setAlert({
        show: true,
        type: 'success',
        message: t('contact.submitSuccess'),
      });

      // 2초 후 닫기
      setTimeout(() => {
        resetForm();
        onClose();
      }, 2000);
    } catch (error) {
      console.error('문의 전송 실패:', error);
      setAlert({
        show: true,
        type: 'error',
        message: t('contact.submitError'),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {t('contact.dialogTitle')}
        <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
          {t('contact.dialogSubtitle')}
        </Typography>
      </DialogTitle>

      <DialogContent>
        {/* 문의 유형 선택 */}
        <FormControl fullWidth sx={{ mt: 2 }}>
          <InputLabel id="contact-category-label">{t('contact.categoryLabel')}</InputLabel>
          <Select
            labelId="contact-category-label"
            value={category}
            label={t('contact.categoryLabel')}
            onChange={(e) => setCategory(e.target.value)}
          >
            <MenuItem value="general">{t('contact.categories.general')}</MenuItem>
            <MenuItem value="bug">{t('contact.categories.bug')}</MenuItem>
            <MenuItem value="feature">{t('contact.categories.feature')}</MenuItem>
            <MenuItem value="data">{t('contact.categories.data')}</MenuItem>
            <MenuItem value="other">{t('contact.categories.other')}</MenuItem>
          </Select>
        </FormControl>

        {/* 제목 입력 */}
        <TextField
          fullWidth
          label={t('contact.titleLabel')}
          placeholder={t('contact.titlePlaceholder')}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          sx={{ mt: 2 }}
          inputProps={{ maxLength: 100 }}
          helperText={`${title.length}/100`}
        />

        {/* 내용 입력 */}
        <TextField
          multiline
          rows={6}
          fullWidth
          label={t('contact.contentLabel')}
          placeholder={t('contact.contentPlaceholder')}
          value={content}
          onChange={(e) => setContent(e.target.value)}
          sx={{ mt: 2 }}
          inputProps={{ maxLength: 2000 }}
          helperText={`${content.length}/2000`}
        />

        {/* 이메일 입력 (선택) */}
        <TextField
          fullWidth
          type="email"
          label={t('contact.emailLabel')}
          placeholder={t('contact.emailPlaceholder')}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          sx={{ mt: 2 }}
          helperText={t('contact.emailHelper')}
        />

        {/* 안내 메시지 */}
        <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
          <Typography variant="caption" color="text.secondary">
            💡 {t('contact.notice')}
          </Typography>
        </Box>

        {/* 알림 */}
        {alert.show && (
          <Alert severity={alert.type} sx={{ mt: 2 }}>
            {alert.message}
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={isSubmitting}>
          {t('common:cancel', '취소')}
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isSubmitting || !title.trim() || !content.trim()}
        >
          {isSubmitting ? t('common:submitting', '전송 중...') : t('contact.submitButton')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ContactDeveloperDialog;
